import { Send } from 'lucide-react';
import { useState } from 'react';

import type { CandidatePathParams } from '@/modules/candidate-detail/queries/types';
import type {
  CandidateDecision,
  PendingDecisionAction,
} from '@/modules/candidate-detail/types';
import { useCandidateJobQuery } from '@/modules/candidates/queries/useCandidateJobQuery';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import { Button } from '@/modules/ui/components/button/Button';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import { OutreachDraftsSheet } from '@/pages/hm/job-candidates/OutreachDraftsSheet';
import { useIsManualOutreach } from '@/pages/hm/job-candidates/useIsManualOutreach';

/** Decisions that take the candidate out of the process: no outreach after them. */
const closedDecisions: CandidateDecision[] = ['rejected', 'declined', 'hired'];

interface CandidateDraftReviewProps {
  decision: CandidateDecision;
  params: CandidatePathParams;
  /** A decision in flight, such as a rejection, blocks sending until it settles. */
  pendingAction: PendingDecisionAction;
  /** Query string to carry through internal links (tenant selection). */
  searchSuffix: string;
}

/**
 * Review & send for this candidate's draft on a job where the customer does
 * the outreach, in the same sheet as the candidate list but with only this
 * person in it. The draft lives on the candidate job's `reachOutMessage`; the
 * brief does not carry it. Renders nothing until both reads succeed: it is a
 * secondary action, and the page's brief owns load errors.
 */
export function CandidateDraftReview({
  decision,
  params,
  pendingAction,
  searchSuffix,
}: CandidateDraftReviewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const jobQuery = useJobQuery(params.jobId);
  const isManualOutreach = useIsManualOutreach(jobQuery.data);
  const candidateJobQuery = useCandidateJobQuery(params, {
    enabled: isManualOutreach,
  });
  const { linkedInStatus, retryLinkedInLookup } = useIsLinkedInConnected();

  const job = jobQuery.data;
  const candidateJob = candidateJobQuery.data;
  if (!isManualOutreach || !job || !candidateJob) return null;
  const hasDraft = candidateJob.reachOutMessage?.status === 'Draft';
  const isClosed = closedDecisions.includes(decision);
  // Gate the send path too, not only the button: the sheet gets no drafts
  // and stays closed, so a rejection and an outreach can never race.
  const canSend = hasDraft && !isClosed && pendingAction === null;

  return (
    <>
      {hasDraft && !isClosed && (
        <Button
          className="h-8 rounded-md"
          disabled={!canSend}
          onClick={() => setIsOpen(true)}
          size="sm"
        >
          <Send className="size-3.5" />
          Review & send
        </Button>
      )}
      {/* Mounted after the send too, so the sheet can animate closed. */}
      <OutreachDraftsSheet
        drafts={canSend ? [candidateJob] : []}
        integrationsHref={`/hm/integrations${searchSuffix}`}
        isOpen={isOpen && canSend}
        job={job}
        linkedInStatus={linkedInStatus}
        onOpenChange={setIsOpen}
        onRetryLinkedIn={retryLinkedInLookup}
      />
    </>
  );
}
